import { useState, useCallback, useRef, useMemo, memo } from 'react';
import { Flex, Button } from '@backstage/ui';
import { useTranslationRef } from '@backstage/core-plugin-api/alpha';
import { RiPlayLine } from '@remixicon/react';
import CodeMirror from '@uiw/react-codemirror';
import { sql, PostgreSQL } from '@codemirror/lang-sql';
import { tokyoNight } from '@uiw/codemirror-theme-tokyo-night';
import { keymap, EditorView, tooltips } from '@codemirror/view';
import { Prec } from '@codemirror/state';
import { appTranslationRef } from '../../../../core/i18n';
import { createColumnCompletion } from './sql-autocomplete.util';
import type { DbSchemaResponse } from './db-explorer.types';
import styles from './sql-editor.module.css';

const isMac = typeof navigator !== 'undefined' && /Mac|iPod|iPhone|iPad/.test(navigator.userAgent);

const ShortcutKbd = memo(({ label }: { label: string }) => (
  <kbd className={styles.kbd}>
    {label}
  </kbd>
));

interface SqlEditorProps {
  onExecute: (sql: string) => void;
  loading: boolean;
  disabled?: boolean;
  stretch?: boolean;
  schema: DbSchemaResponse | null;
}

export const SqlEditor = ({ onExecute, loading, disabled, stretch, schema }: SqlEditorProps) => {
  const { t } = useTranslationRef(appTranslationRef);
  const [value, setValue] = useState('');

  const handleSubmit = useCallback(() => {
    const trimmed = value.trim();
    if (trimmed) {
      onExecute(trimmed);
    }
  }, [value, onExecute]);

  const schemaMap = useMemo(() => {
    if (!schema) return undefined;
    const map: Record<string, string[]> = {};
    for (const table of schema.tables) {
      map[table.name] = table.columns.map(c => c.name);
    }
    return map;
  }, [schema]);

  const sqlExtension = useMemo(
    () => sql({
      dialect: PostgreSQL,
      schema: schemaMap,
      keywordCompletion: (label, type) => ({ label, type, boost: -1 }),
    }),
    [schemaMap],
  );

  const columnCompletion = useMemo(
    () => schemaMap ? createColumnCompletion(schemaMap) : [],
    [schemaMap],
  );

  const handleSubmitRef = useRef(handleSubmit);
  handleSubmitRef.current = handleSubmit;

  const execKeymap = useMemo(
    () => Prec.highest(keymap.of([{
      key: 'Ctrl-Enter',
      mac: 'Cmd-Enter',
      run: () => { handleSubmitRef.current(); return true; },
    }])),
    [],
  );

  // Fixed positioning lets tooltips escape the overflow:hidden wrapper
  const fixedTooltips = useMemo(() => tooltips({ position: 'fixed' }), []);

  const fontTheme = useMemo(
    () => EditorView.theme({
      '&, .cm-content, .cm-gutters, .cm-tooltip, .cm-tooltip-autocomplete, .cm-tooltip-autocomplete ul, .cm-tooltip-autocomplete ul li, .cm-completionLabel, .cm-completionDetail, .cm-placeholder': {
        fontFamily: "'JetBrains Mono', var(--bui-font-monospace)",
        fontSize: '13px',
      },
    }),
    [],
  );

  const extensions = useMemo(
    () => [sqlExtension, columnCompletion, execKeymap, fixedTooltips],
    [sqlExtension, columnCompletion, execKeymap, fixedTooltips],
  );

  const theme = useMemo(
    () => [tokyoNight, fontTheme],
    [fontTheme],
  );

  return (
    <Flex direction="column" gap="3" className={stretch ? styles.editorFlex : undefined}>
      <div className={`${styles.editorWrap}${stretch ? ` ${styles.editorWrapStretch}` : ''}`}>
        <CodeMirror
          value={value}
          onChange={setValue}
          theme={theme}
          extensions={extensions}
          placeholder={t('dbExplorer.sqlEditor.placeholder')}
          height={stretch ? '100%' : '180px'}
          basicSetup={{
            lineNumbers: true,
            foldGutter: false,
            highlightActiveLine: true,
            autocompletion: true,
          }}
          editable={!loading && !disabled}
        />
      </div>
      <Flex justify="end" align="center">
        <Button
          onPress={handleSubmit}
          isDisabled={loading || disabled || !value.trim()}
          iconStart={<RiPlayLine size={16} />}
        >
          {loading ? t('dbExplorer.sqlEditor.running') : t('dbExplorer.sqlEditor.run')}
          {!loading && <ShortcutKbd label={isMac ? '⌘↵' : 'Ctrl+↵'} />}
        </Button>
      </Flex>
    </Flex>
  );
};
